import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import styles from "@components/account/Account.module.css";
import BaseErrors from "@components/fields/BaseErrors";
import DataSelect from "@components/fields/DataSelect";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import LocationAutocompleteInput from "@components/fields/LocationAutocompleteInput";
import PhoneInput from "@components/fields/PhoneInput";
import Form from "@components/forms/Form";
import {
  ClientUpdateDocument,
  ClientUpdateMutation,
  ClientUpdateMutationVariables,
  GetClientWithAccountQuery,
  GetJourneyWithCareProfileQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  email: string;
  firstName: string;
  lastName: string;
  number?: string | null;
  placeId: string | null;
  timezone: string | null;
};

type Props = {
  autofocus: boolean;
  client: GetClientWithAccountQuery["client"] | GetJourneyWithCareProfileQuery["journey"]["clientRoles"][0]["client"];
};

const AccountForm = ({ autofocus, client }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [clientUpdate] = useMutation<ClientUpdateMutation, ClientUpdateMutationVariables>(ClientUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      email: yup
        .string()
        .email(intl.formatMessage({ id: "errors.registration.email.notAnEmail" }))
        .required(intl.formatMessage({ id: "errors.registration.email.blank" })),
      firstName: yup.string().required(intl.formatMessage({ id: "errors.registration.firstName.blank" })),
      lastName: yup.string().required(intl.formatMessage({ id: "errors.registration.lastName.blank" })),
      number: yup
        .string()
        .nullable()
        .test(
          "is-valid-phone",
          intl.formatMessage({ id: "errors.clientAccountUpdate.number.invalid" }),
          value => !value || isValidPhoneNumber(value)
        ),
      placeId: yup.string().required(intl.formatMessage({ id: "errors.careProfile.placeId.blank" })),
      timezone: yup.string().required(intl.formatMessage({ id: "errors.registration.timezone.blank" }))
    })
    .required();

  const calculateDefaultValues = useCallback(
    (): FormData => ({
      email: client.email.email,
      firstName: client.firstName,
      lastName: client.lastName,
      number: client.consultPhoneNumber?.number ?? null,
      placeId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS ? "ChIJfX6-HwSbf4gR4Aa9pkeWBLY" : client.location.placeId,
      timezone: client.timezone
    }),
    [client]
  );

  const defaultValues = calculateDefaultValues();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const resetForm = useCallback(() => {
    const values = calculateDefaultValues();
    form.reset(values);
  }, [calculateDefaultValues, form]);

  const number = form.watch("number");

  useEffect(() => {
    resetForm();
  }, [resetForm, client.firstName, client.lastName, client.email.email]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    return clientUpdate({
      variables: {
        ...form.getValues(),
        formattedNumber: number ? formatPhoneNumber(number) : undefined,
        id: client.id,
        number
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.clientUpdate,
        intl,
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.clientAccountUpdate"
      });
    });
  }, [client.id, clientUpdate, form, intl, number]);

  return (
    <Form data-test="account-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <IonGrid>
        <IonRow className={styles.accountFormRow}>
          <IonCol>
            <div className={styles.columnSpacing}>
              <Input<FormData>
                defaultValue={defaultValues.firstName}
                error={form.formState.errors.firstName}
                label={intl.formatMessage({ id: "forms.clientAccountUpdate.firstNameLabel" })}
                noSpacing
                register={form.register("firstName")}
                required
              />
            </div>
          </IonCol>
          <IonCol>
            <Input<FormData>
              defaultValue={defaultValues.lastName}
              error={form.formState.errors.lastName}
              label={intl.formatMessage({ id: "forms.clientAccountUpdate.lastNameLabel" })}
              noSpacing
              register={form.register("lastName")}
              required
            />
          </IonCol>
        </IonRow>
        <IonRow className={styles.accountFormRow}>
          <IonCol>
            <Input<FormData>
              autofocus={autofocus}
              data-test="email-input"
              defaultValue={defaultValues.email}
              error={form.formState.errors.email}
              label={intl.formatMessage({ id: "forms.clientAccountUpdate.emailLabel" })}
              noSpacing
              register={form.register("email")}
              required
              type="email"
            />
          </IonCol>
        </IonRow>
        <IonRow className={styles.accountFormRow}>
          <IonCol>
            <PhoneInput<FormData>
              control={form.control}
              error={form.formState.errors.number}
              label={intl.formatMessage({ id: "forms.clientAccountUpdate.numberLabel" })}
              labelPlacement="stacked"
              name="number"
              position="stacked"
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol sizeMd="8" sizeSm="12" sizeXs="12">
            <div className={`${styles.columnSpacing} ${styles.noMarginMobile}`}>
              <LocationAutocompleteInput
                control={form.control}
                error={form.formState.errors.placeId}
                label={intl.formatMessage({ id: "forms.clientAccountUpdate.clientLocationLabel" })}
                labelPlacement="stacked"
                location={{ address: client.location.address, placeId: client.location.placeId }}
                name="placeId"
                noResults={intl.formatMessage({ id: "errors.careProfile.placeId.noResults" })}
                placeholder={intl.formatMessage({ id: "forms.careProfile.clientLocationPlaceholder" })}
                required
              />
            </div>
          </IonCol>
          <IonCol sizeMd="4" sizeSm="12" sizeXs="12">
            <DataSelect<FormData>
              control={form.control}
              error={form.formState.errors.timezone}
              label={intl.formatMessage({ id: "forms.clientAccountUpdate.timezoneLabel" })}
              labelPlacement="stacked"
              name="timezone"
              required
              type="timezones"
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      <FormButtons hookForm={form} onCancel={resetForm} skipCancel skipReset />
    </Form>
  );
};

export default AccountForm;
